// ─── Navigation ───────────────────────────────────────────────────────────────

import { PaymentCard } from "@/features/payment/types";

export type Screen =
  | "search"
  | "results"
  | "checkin"
  | "collect-payment"
  | "payment-plan"
  | "other-amount"
  | "update-card"
  | "payment-success"
  | "update-card-success";

export type AppTab =
  | "frontdesk"
  | "dashboard"
  | "schedule"
  | "checkin"
  | "patients"
  | "billing"
  | "reports"
  | "settings"
  | "owner"
  | "manager"
  | "clinical"
  | "today"
  | "claims"
  | "waitlist"
  | "tasks"
  | "tasks3"
  | "tasks5"
  | "tasks6"
  | "tasks10"
  | "tasks11"
  | "tasks12"
  | "tasks13"
  | "tasks14"
  | "tasks15";

// ─── Core Models ──────────────────────────────────────────────────────────────
export interface Claim {
  id: string;
  code: string;
  description: string;
  date: string;
  postedDate: string;
  amount: number;
  status: "pending-insurance" | "patient-balance";
  provider: string;
  lineItems?: {
    label: string;
    amount: number;
    type: "charge" | "payment" | "adjustment";
  }[];
}

export interface Patient {
  id: string;
  name: string;
  initials: string;
  dob: string;
  mrn: string;
  phone: string;
  address: string;
  insurance: string;
  insuranceId?: string;
  secondaryInsurance?: string;
  insuranceStatus: "active" | "inactive" | "pending";
  provider: string;
  location: string;
  email: string;
  appointmentDate: string;
  appointmentType: string;
  balanceDue: number;
  copayDue: number;
  pastDue: number;
  deductibleMet: number;
  deductibleTotal: number;
  cardOnFile: PaymentCard;
  claims: Claim[];
  lastVisit?: string;
  totalVisits?: number;
  notes?: string;
}

export type AppointmentStatus =
  | "scheduled"
  | "checked-in"
  | "in-progress"
  | "completed"
  | "no-show";
export type CheckInStep = "verify" | "insurance" | "collect" | "complete";

export interface Appointment {
  id: string;
  time: string;
  patientName: string;
  patientInitials: string;
  patientId: string;
  type: string;
  provider: string;
  room?: string;
  status: AppointmentStatus;
  balanceDue: number;
  copayDue: number;
  hasExpiredCard: boolean;
  hasInsuranceIssue: boolean;
  duration: number;
  checkInStep?: CheckInStep;
  waitMinutes?: number;
}

// ─── Billing / Claims ─────────────────────────────────────────────────────────

export type BillingClaimStatus =
  | "submitted"
  | "pending"
  | "approved"
  | "partial"
  | "denied"
  | "patient-balance"
  | "closed"
  | "needs-auth";

export type DenialCode =
  | "CO-4"
  | "CO-16"
  | "CO-50"
  | "CO-97"
  | "PR-1"
  | "PR-2"
  | "PR-3"
  | "OA-23";

export const DENIAL_DESCRIPTIONS: Record<
  DenialCode,
  { short: string; action: string }
> = {
  "CO-4": {
    short: "Modifier missing or invalid",
    action: "Review and resubmit with correct modifier",
  },
  "CO-16": {
    short: "Missing required information",
    action: "Add missing info and resubmit",
  },
  "CO-50": {
    short: "Not medically necessary",
    action: "Submit medical records and appeal",
  },
  "CO-97": {
    short: "Benefit included in primary procedure",
    action: "Remove duplicate charge or appeal with documentation",
  },
  "PR-1": {
    short: "Patient deductible applies",
    action: "Bill patient for deductible amount",
  },
  "PR-2": {
    short: "Patient coinsurance applies",
    action: "Bill patient for coinsurance amount",
  },
  "PR-3": {
    short: "Patient copay applies",
    action: "Collect copay from patient",
  },
  "OA-23": {
    short: "Payment adjusted — capitation agreement",
    action: "No action — contractual",
  },
};

export interface BillingClaim {
  id: string;
  patientName: string;
  patientInitials: string;
  patientId: string;
  serviceDate: string;
  postedDate: string;
  cptCode: string;
  description: string;
  provider: string;
  payer: string;
  billed: number;
  insurancePaid: number;
  adjustment: number;
  patientOwes: number;
  status: BillingClaimStatus;
  denialCode?: DenialCode;
  denialNote?: string;
  daysOutstanding: number;
  priorAuthNumber?: string;
  priorAuthExpiry?: string;
  priorAuthVisitsUsed?: number;
  priorAuthVisitsTotal?: number;
}

export interface PriorAuth {
  id: string;
  patientName: string;
  patientInitials: string;
  patientId: string;
  authNumber: string;
  procedure: string;
  cptCode: string;
  payer: string;
  provider: string;
  startDate: string;
  expiryDate: string;
  visitsAuthorized: number;
  visitsUsed: number;
  status: "active" | "expiring" | "expired" | "pending";
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export interface MonthlyCollection {
  month: string;
  collected: number;
  billed: number;
  goal: number;
}
export interface ProviderStats {
  name: string;
  visits: number;
  billed: number;
  collected: number;
  collectionRate: number;
  noShows: number;
}
export interface PayerPerformance {
  payer: string;
  claims: number;
  avgDaysToPay: number;
  denialRate: number;
  collectionRate: number;
}
export interface AgingBucket {
  label: string;
  amount: number;
  count: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

export const MOCK_PATIENTS: Patient[] = [
  {
    id: "P001",
    name: "Michael Thompson",
    initials: "MT",
    dob: "08/15/1983",
    mrn: "MRN022934000",
    phone: "586-555-4682",
    address: "4582 Maple Avenue\nRoyal Oak, MI 48073",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ123456789",
    insuranceStatus: "active",
    deductibleMet: 850,
    deductibleTotal: 1500,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "m.thompson@email.com",
    appointmentDate: "11/15/1987",
    appointmentType: "Follow-up",
    lastVisit: "Mar 23",
    totalVisits: 4,
    balanceDue: 225.0,
    copayDue: 25.0,
    pastDue: 200.0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    notes: "Prefers morning appointments. Latex allergy.",
    claims: [
      {
        id: "C001",
        code: "11302",
        description: "Lesion Removal (shave)",
        date: "12/15/2025",
        postedDate: "12/15/2025",
        amount: 200.0,
        status: "pending-insurance",
        provider: "DR. SARAH MITCHELL",
      },
      {
        id: "C002",
        code: "99213",
        description: "Office Visit - Follow-up",
        date: "12/15/2025",
        postedDate: "12/15/2025",
        amount: 25.0,
        status: "patient-balance",
        provider: "DR. SARAH MITCHELL",
        lineItems: [
          {
            label: "99213 – Office Visit – Follow-up",
            amount: 325.0,
            type: "charge",
          },
          { label: "Insurance Payment – BCBS", amount: 260.0, type: "payment" },
          { label: "Insurance Adjustment", amount: 40.0, type: "adjustment" },
          { label: "Patient Payment", amount: 0, type: "payment" },
        ],
      },
    ],
  },
  {
    id: "P002",
    name: "Alice Thompson",
    initials: "AT",
    dob: "09/20/1991",
    mrn: "MRN022934089",
    phone: "586-555-4682",
    address: "4582 Maple Avenue\nRoyal Oak, MI 48073",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ987654321",
    insuranceStatus: "active",
    deductibleMet: 0,
    deductibleTotal: 1000,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "a.thompson@email.com",
    appointmentDate: "09/20/1991",
    appointmentType: "Annual Physical",
    lastVisit: "01/10/2025",
    totalVisits: 3,
    balanceDue: 0,
    copayDue: 20,
    pastDue: 0,
    cardOnFile: {
      brand: "MC",
      last4: "8891",
      expMonth: 8,
      expYear: 2026,
      cardHolderName: "Alice Thompson",
    },
    claims: [],
  },
  {
    id: "P003",
    name: "Sarah Thompson",
    initials: "ST",
    dob: "01/08/2008",
    mrn: "MRN022934105",
    phone: "586-555-4682",
    address: "4582 Maple Avenue\nRoyal Oak, MI 48073",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ111222333",
    insuranceStatus: "active",
    deductibleMet: 200,
    deductibleTotal: 500,
    provider: "Dr. Emily Chen",
    location: "Base Dermatology • EST",
    email: "s.thompson@email.com",
    appointmentDate: "01/08/2008",
    appointmentType: "Specialist Referral",
    lastVisit: "11/05/2025",
    totalVisits: 12,
    balanceDue: 0,
    copayDue: 40,
    pastDue: 0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P004",
    name: "Michael Johnson",
    initials: "MJ",
    dob: "02/28/1987",
    mrn: "MRN022934051",
    phone: "734-555-2468",
    address: "123 Main St\nDetroit, MI 48201",
    insurance: "Aetna",
    insuranceId: "AET445566778",
    insuranceStatus: "active",
    deductibleMet: 1200,
    deductibleTotal: 1500,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "m.johnson@email.com",
    appointmentDate: "02/28/1987",
    appointmentType: "Consultation",
    lastVisit: "08/22/2025",
    totalVisits: 5,
    balanceDue: 0,
    copayDue: 30,
    pastDue: 0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P005",
    name: "Michael Martinez",
    initials: "MM",
    dob: "04/03/1987",
    mrn: "MRN022934077",
    phone: "313-555-3210",
    address: "456 Oak Ave\nDearborn, MI 48124",
    insurance: "United Health",
    insuranceId: "UHC778899001",
    insuranceStatus: "active",
    deductibleMet: 400,
    deductibleTotal: 2000,
    provider: "Dr. Emily Chen",
    location: "Base Dermatology • EST",
    email: "m.martinez@email.com",
    appointmentDate: "04/03/1987",
    appointmentType: "Follow-up",
    lastVisit: "10/15/2025",
    totalVisits: 6,
    balanceDue: 0,
    copayDue: 20,
    pastDue: 0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P006",
    name: "Jennifer Walsh",
    initials: "JW",
    dob: "06/12/1979",
    mrn: "MRN022934120",
    phone: "248-555-7890",
    address: "789 Pine St\nBirmingham, MI 48009",
    insurance: "Cigna",
    insuranceId: "CIG334455667",
    insuranceStatus: "active",
    deductibleMet: 0,
    deductibleTotal: 1000,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "j.walsh@email.com",
    appointmentDate: "06/12/1979",
    appointmentType: "New Patient Consult",
    totalVisits: 1,
    balanceDue: 40,
    copayDue: 40,
    pastDue: 0,
    cardOnFile: {
      brand: "VISA",
      last4: "2241",
      expMonth: 9,
      expYear: 2027,
      cardHolderName: "Jennifer Walsh",
    },
    claims: [],
  },
  {
    id: "P007",
    name: "Robert Chen",
    initials: "RC",
    dob: "03/22/1995",
    mrn: "MRN022934131",
    phone: "313-555-4412",
    address: "321 Oak Blvd\nAnna Arbor, MI 48104",
    insurance: "Aetna",
    insuranceId: "AET556677889",
    insuranceStatus: "pending",
    deductibleMet: 600,
    deductibleTotal: 1500,
    provider: "Dr. Emily Chen",
    location: "Base Dermatology • EST",
    email: "r.chen@email.com",
    appointmentDate: "03/22/1995",
    appointmentType: "Acne Treatment",
    lastVisit: "07/30/2025",
    totalVisits: 4,
    balanceDue: 145,
    copayDue: 35,
    pastDue: 145,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P008",
    name: "Maria Gonzalez",
    initials: "MG",
    dob: "11/30/1988",
    mrn: "MRN022934142",
    phone: "586-555-3321",
    address: "654 Elm Dr\nWarren, MI 48088",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ444555666",
    insuranceStatus: "active",
    deductibleMet: 300,
    deductibleTotal: 1000,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "m.gonzalez@email.com",
    appointmentDate: "11/30/1988",
    appointmentType: "Eczema Consult",
    lastVisit: "06/14/2025",
    totalVisits: 7,
    balanceDue: 0,
    copayDue: 30,
    pastDue: 0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P009",
    name: "David Park",
    initials: "DP",
    dob: "07/04/1970",
    mrn: "MRN022934153",
    phone: "734-555-8876",
    address: "987 Birch Ln\nAnn Arbor, MI 48103",
    insurance: "United Health",
    insuranceId: "UHC223344556",
    insuranceStatus: "active",
    deductibleMet: 1800,
    deductibleTotal: 2000,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "d.park@email.com",
    appointmentDate: "07/04/1970",
    appointmentType: "Skin Check",
    lastVisit: "03/22/2025",
    totalVisits: 15,
    balanceDue: 320,
    copayDue: 25,
    pastDue: 320,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P010",
    name: "James Wilson",
    initials: "JW",
    dob: "05/17/1982",
    mrn: "MRN022934164",
    phone: "248-555-6634",
    address: "210 Cedar Rd\nTroy, MI 48083",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ667788990",
    insuranceStatus: "active",
    deductibleMet: 950,
    deductibleTotal: 1500,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "j.wilson@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Mole Removal",
    lastVisit: "01/15/2026",
    totalVisits: 3,
    balanceDue: 175,
    copayDue: 50,
    pastDue: 175,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P011",
    name: "Lisa Anderson",
    initials: "LA",
    dob: "12/09/1975",
    mrn: "MRN022934175",
    phone: "313-555-9921",
    address: "567 Walnut St\nSouthfield, MI 48075",
    insurance: "Aetna",
    insuranceId: "AET889900112",
    insuranceStatus: "active",
    deductibleMet: 700,
    deductibleTotal: 1000,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "l.anderson@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Psoriasis Follow-up",
    lastVisit: "02/10/2026",
    totalVisits: 8,
    balanceDue: 89,
    copayDue: 25,
    pastDue: 89,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P012",
    name: "Kevin Brown",
    initials: "KB",
    dob: "08/23/1993",
    mrn: "MRN022934186",
    phone: "586-555-7712",
    address: "890 Spruce Dr\nSterling Heights, MI 48310",
    insurance: "Cigna",
    insuranceId: "CIG112233445",
    insuranceStatus: "active",
    deductibleMet: 300,
    deductibleTotal: 1500,
    provider: "Dr. Emily Chen",
    location: "Base Dermatology • EST",
    email: "k.brown@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Rosacea Treatment",
    lastVisit: "12/05/2025",
    totalVisits: 2,
    balanceDue: 0,
    copayDue: 35,
    pastDue: 0,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P013",
    name: "Amanda Torres",
    initials: "AT",
    dob: "03/14/1990",
    mrn: "MRN022934197",
    phone: "248-555-3345",
    address: "432 Ash Ln\nRochester Hills, MI 48307",
    insurance: "United Health",
    insuranceId: "UHC556677889",
    insuranceStatus: "active",
    deductibleMet: 0,
    deductibleTotal: 2000,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "a.torres@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "New Patient Consult",
    totalVisits: 0,
    balanceDue: 260,
    copayDue: 40,
    pastDue: 260,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P014",
    name: "Emma Davis",
    initials: "ED",
    dob: "11/02/1998",
    mrn: "MRN022934208",
    phone: "734-555-1198",
    address: "765 Hickory Ct\nYpsilanti, MI 48197",
    insurance: "Blue Cross Blue Shield",
    insuranceId: "XYZ998877665",
    insuranceStatus: "active",
    deductibleMet: 500,
    deductibleTotal: 1000,
    provider: "Dr. Emily Chen",
    location: "Base Dermatology • EST",
    email: "e.davis@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Wart Removal",
    lastVisit: "11/20/2025",
    totalVisits: 4,
    balanceDue: 210,
    copayDue: 25,
    pastDue: 210,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P015",
    name: "Nicole White",
    initials: "NW",
    dob: "09/28/1985",
    mrn: "MRN022934219",
    phone: "313-555-5567",
    address: "111 Magnolia Ave\nFerndale, MI 48220",
    insurance: "Aetna",
    insuranceId: "AET334455667",
    insuranceStatus: "active",
    deductibleMet: 1100,
    deductibleTotal: 1500,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "n.white@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Botox Consult",
    lastVisit: "09/12/2025",
    totalVisits: 6,
    balanceDue: 150,
    copayDue: 45,
    pastDue: 150,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P016",
    name: "Rachel Patel",
    initials: "RP",
    dob: "04/19/1992",
    mrn: "MRN022934230",
    phone: "248-555-8843",
    address: "234 Poplar St\nNovi, MI 48375",
    insurance: "United Health",
    insuranceId: "UHC445566778",
    insuranceStatus: "active",
    deductibleMet: 450,
    deductibleTotal: 1000,
    provider: "Dr. Sarah Mitchell",
    location: "Base Dermatology • EST",
    email: "r.patel@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Skin Check",
    lastVisit: "01/30/2026",
    totalVisits: 3,
    balanceDue: 95,
    copayDue: 30,
    pastDue: 95,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
  {
    id: "P017",
    name: "Daniel Kim",
    initials: "DK",
    dob: "10/14/1989",
    mrn: "MRN022934241",
    phone: "734-555-6601",
    address: "876 Willow Dr\nPlymouth, MI 48170",
    insurance: "Cigna",
    insuranceId: "CIG667788990",
    insuranceStatus: "active",
    deductibleMet: 200,
    deductibleTotal: 1500,
    provider: "Dr. James Roberts",
    location: "Base Dermatology • EST",
    email: "d.kim@email.com",
    appointmentDate: "03/26/2026",
    appointmentType: "Cyst Removal",
    lastVisit: "04/18/2025",
    totalVisits: 2,
    balanceDue: 180,
    copayDue: 35,
    pastDue: 180,
    cardOnFile: {
      brand: "",
      last4: "",
      expMonth: 0,
      expYear: 0,
      cardHolderName: "",
    },
    claims: [],
  },
];

export const MOCK_APPOINTMENTS: Appointment[] = [
  {
    id: "A001",
    time: "8:30 AM",
    patientName: "Jennifer Walsh",
    patientInitials: "JW",
    patientId: "P006",
    type: "New Patient Consult",
    provider: "Dr. Sarah Mitchell",
    room: "Exam 1",
    status: "checked-in",
    balanceDue: 0,
    copayDue: 40,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 45,
    waitMinutes: 0,
  },
  {
    id: "A002",
    time: "9:00 AM",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    patientId: "P001",
    type: "Follow-up Visit",
    provider: "Dr. Sarah Mitchell",
    room: "Exam 2",
    status: "scheduled",
    balanceDue: 225,
    copayDue: 25,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
    waitMinutes: 0,
  },
  {
    id: "A003",
    time: "9:30 AM",
    patientName: "Alice Thompson",
    patientInitials: "AT",
    patientId: "P002",
    type: "Annual Physical",
    provider: "Dr. James Roberts",
    room: "Exam 3",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 20,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
    waitMinutes: 0,
  },
  {
    id: "A004",
    time: "10:00 AM",
    patientName: "Robert Chen",
    patientInitials: "RC",
    patientId: "P007",
    type: "Acne Treatment",
    provider: "Dr. Emily Chen",
    status: "scheduled",
    balanceDue: 145,
    copayDue: 35,
    hasExpiredCard: false,
    hasInsuranceIssue: true,
    duration: 20,
  },
  {
    id: "A005",
    time: "10:15 AM",
    patientName: "Maria Gonzalez",
    patientInitials: "MG",
    patientId: "P008",
    type: "Eczema Consult",
    provider: "Dr. Sarah Mitchell",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 30,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A006",
    time: "10:45 AM",
    patientName: "David Park",
    patientInitials: "DP",
    patientId: "P009",
    type: "Skin Check",
    provider: "Dr. James Roberts",
    status: "scheduled",
    balanceDue: 320,
    copayDue: 25,
    hasExpiredCard: true,
    hasInsuranceIssue: false,
    duration: 20,
  },
  {
    id: "A007",
    time: "11:00 AM",
    patientName: "Sarah Thompson",
    patientInitials: "ST",
    patientId: "P003",
    type: "Specialist Referral",
    provider: "Dr. Emily Chen",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 40,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 45,
  },
  {
    id: "A008",
    time: "11:30 AM",
    patientName: "James Wilson",
    patientInitials: "JW",
    patientId: "P010",
    type: "Mole Removal",
    provider: "Dr. Sarah Mitchell",
    status: "scheduled",
    balanceDue: 175,
    copayDue: 50,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A009",
    time: "1:00 PM",
    patientName: "Lisa Anderson",
    patientInitials: "LA",
    patientId: "P011",
    type: "Psoriasis Follow-up",
    provider: "Dr. James Roberts",
    status: "scheduled",
    balanceDue: 89,
    copayDue: 25,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A010",
    time: "1:30 PM",
    patientName: "Kevin Brown",
    patientInitials: "KB",
    patientId: "P012",
    type: "Rosacea Treatment",
    provider: "Dr. Emily Chen",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 35,
    hasExpiredCard: false,
    hasInsuranceIssue: true,
    duration: 20,
  },
  {
    id: "A011",
    time: "2:00 PM",
    patientName: "Amanda Torres",
    patientInitials: "AT",
    patientId: "P013",
    type: "New Patient Consult",
    provider: "Dr. Sarah Mitchell",
    status: "scheduled",
    balanceDue: 260,
    copayDue: 40,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 45,
  },
  {
    id: "A012",
    time: "2:30 PM",
    patientName: "Michael Johnson",
    patientInitials: "MJ",
    patientId: "P004",
    type: "Consultation",
    provider: "Dr. James Roberts",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 30,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A013",
    time: "3:00 PM",
    patientName: "Emma Davis",
    patientInitials: "ED",
    patientId: "P014",
    type: "Wart Removal",
    provider: "Dr. Emily Chen",
    status: "scheduled",
    balanceDue: 210,
    copayDue: 25,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 20,
  },
  {
    id: "A014",
    time: "3:30 PM",
    patientName: "Michael Martinez",
    patientInitials: "MM",
    patientId: "P005",
    type: "Follow-up",
    provider: "Dr. Sarah Mitchell",
    status: "scheduled",
    balanceDue: 0,
    copayDue: 20,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A015",
    time: "4:00 PM",
    patientName: "Nicole White",
    patientInitials: "NW",
    patientId: "P015",
    type: "Botox Consult",
    provider: "Dr. James Roberts",
    status: "scheduled",
    balanceDue: 150,
    copayDue: 45,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
  {
    id: "A016",
    time: "10:00 AM",
    patientName: "Rachel Patel",
    patientInitials: "RP",
    patientId: "P016",
    type: "Skin Check",
    provider: "Dr. Sarah Mitchell",
    status: "scheduled",
    balanceDue: 95,
    copayDue: 30,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 20,
  },
  {
    id: "A017",
    time: "4:30 PM",
    patientName: "Daniel Kim",
    patientInitials: "DK",
    patientId: "P017",
    type: "Cyst Removal",
    provider: "Dr. James Roberts",
    status: "scheduled",
    balanceDue: 180,
    copayDue: 35,
    hasExpiredCard: false,
    hasInsuranceIssue: false,
    duration: 30,
  },
];

export const MOCK_BILLING_CLAIMS: BillingClaim[] = [
  {
    id: "BC001",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    patientId: "P001",
    serviceDate: "12/15/2025",
    postedDate: "12/16/2025",
    cptCode: "11302",
    description: "Shave removal of dermal lesion",
    provider: "Dr. Sarah Mitchell",
    payer: "Blue Cross Blue Shield",
    billed: 385,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 0,
    status: "pending",
    daysOutstanding: 18,
  },
  {
    id: "BC002",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    patientId: "P001",
    serviceDate: "12/15/2025",
    postedDate: "12/16/2025",
    cptCode: "99213",
    description: "Office Visit – Follow-up (Level 3)",
    provider: "Dr. Sarah Mitchell",
    payer: "Blue Cross Blue Shield",
    billed: 325,
    insurancePaid: 260,
    adjustment: 40,
    patientOwes: 25,
    status: "patient-balance",
    daysOutstanding: 18,
  },
  {
    id: "BC010",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    patientId: "P001",
    serviceDate: "11/20/2025",
    postedDate: "11/22/2025",
    cptCode: "17000",
    description: "Destruction of premalignant lesion",
    provider: "Dr. Sarah Mitchell",
    payer: "Blue Cross Blue Shield",
    billed: 290,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 0,
    status: "denied",
    denialCode: "CO-4",
    denialNote: "Modifier missing — resubmit with correct modifier",
    daysOutstanding: 45,
  },
  {
    id: "BC003",
    patientName: "David Park",
    patientInitials: "DP",
    patientId: "P009",
    serviceDate: "11/10/2025",
    postedDate: "11/12/2025",
    cptCode: "99214",
    description: "Office Visit – Established Patient (Level 4)",
    provider: "Dr. James Roberts",
    payer: "United Health",
    billed: 420,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 320,
    status: "denied",
    denialCode: "CO-50",
    denialNote: "Service not medically necessary per UHC policy LCD L39046",
    daysOutstanding: 53,
  },
  {
    id: "BC004",
    patientName: "Robert Chen",
    patientInitials: "RC",
    patientId: "P007",
    serviceDate: "10/22/2025",
    postedDate: "10/24/2025",
    cptCode: "17000",
    description: "Destruction of premalignant lesion",
    provider: "Dr. Emily Chen",
    payer: "Aetna",
    billed: 290,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 0,
    status: "denied",
    denialCode: "CO-4",
    denialNote: "Modifier -51 required for multiple procedures",
    daysOutstanding: 72,
  },
  {
    id: "BC005",
    patientName: "Jennifer Walsh",
    patientInitials: "JW",
    patientId: "P006",
    serviceDate: "03/05/2026",
    postedDate: "03/05/2026",
    cptCode: "99203",
    description: "New Patient Office Visit (Level 3)",
    provider: "Dr. Sarah Mitchell",
    payer: "Cigna",
    billed: 340,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 40,
    status: "submitted",
    daysOutstanding: 0,
  },
  {
    id: "BC006",
    patientName: "Alice Thompson",
    patientInitials: "AT",
    patientId: "P002",
    serviceDate: "01/10/2025",
    postedDate: "01/12/2025",
    cptCode: "99213",
    description: "Office Visit – Follow-up (Level 3)",
    provider: "Dr. James Roberts",
    payer: "Blue Cross Blue Shield",
    billed: 325,
    insurancePaid: 285,
    adjustment: 40,
    patientOwes: 0,
    status: "closed",
    daysOutstanding: 0,
  },
  {
    id: "BC007",
    patientName: "Maria Gonzalez",
    patientInitials: "MG",
    patientId: "P008",
    serviceDate: "06/14/2025",
    postedDate: "06/16/2025",
    cptCode: "99214",
    description: "Office Visit – Established (Eczema)",
    provider: "Dr. Sarah Mitchell",
    payer: "Blue Cross Blue Shield",
    billed: 420,
    insurancePaid: 350,
    adjustment: 50,
    patientOwes: 20,
    status: "partial",
    daysOutstanding: 3,
  },
  {
    id: "BC008",
    patientName: "Sarah Thompson",
    patientInitials: "ST",
    patientId: "P003",
    serviceDate: "11/05/2025",
    postedDate: "11/07/2025",
    cptCode: "11305",
    description: "Shave removal lesion – trunk",
    provider: "Dr. Emily Chen",
    payer: "Blue Cross Blue Shield",
    billed: 310,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 0,
    status: "needs-auth",
    priorAuthNumber: "AUTH-2025-44892",
    priorAuthExpiry: "12/31/2025",
    priorAuthVisitsUsed: 3,
    priorAuthVisitsTotal: 3,
    daysOutstanding: 21,
  },
  {
    id: "BC009",
    patientName: "Michael Johnson",
    patientInitials: "MJ",
    patientId: "P004",
    serviceDate: "08/22/2025",
    postedDate: "08/24/2025",
    cptCode: "99215",
    description: "Office Visit – Established (Level 5)",
    provider: "Dr. James Roberts",
    payer: "Aetna",
    billed: 520,
    insurancePaid: 0,
    adjustment: 0,
    patientOwes: 0,
    status: "denied",
    denialCode: "CO-16",
    denialNote: "Missing referring provider NPI",
    daysOutstanding: 41,
  },
];

export const MOCK_PRIOR_AUTHS: PriorAuth[] = [
  {
    id: "PA001",
    patientName: "Sarah Thompson",
    patientInitials: "ST",
    patientId: "P003",
    authNumber: "AUTH-2025-44892",
    procedure: "Lesion Shave Removal",
    cptCode: "11305",
    payer: "Blue Cross Blue Shield",
    provider: "Dr. Emily Chen",
    startDate: "10/01/2025",
    expiryDate: "12/31/2025",
    visitsAuthorized: 3,
    visitsUsed: 3,
    status: "expired",
  },
  {
    id: "PA002",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    patientId: "P001",
    authNumber: "AUTH-2026-00124",
    procedure: "Phototherapy (PUVA)",
    cptCode: "96910",
    payer: "Blue Cross Blue Shield",
    provider: "Dr. Sarah Mitchell",
    startDate: "01/01/2026",
    expiryDate: "06/30/2026",
    visitsAuthorized: 24,
    visitsUsed: 6,
    status: "active",
  },
  {
    id: "PA003",
    patientName: "David Park",
    patientInitials: "DP",
    patientId: "P009",
    authNumber: "AUTH-2026-00891",
    procedure: "Mohs Surgery",
    cptCode: "17311",
    payer: "United Health",
    provider: "Dr. James Roberts",
    startDate: "02/15/2026",
    expiryDate: "03/15/2026",
    visitsAuthorized: 1,
    visitsUsed: 0,
    status: "expiring",
  },
  {
    id: "PA004",
    patientName: "Maria Gonzalez",
    patientInitials: "MG",
    patientId: "P008",
    authNumber: "AUTH-2026-01102",
    procedure: "Dupilumab Injection",
    cptCode: "J0222",
    payer: "Blue Cross Blue Shield",
    provider: "Dr. Sarah Mitchell",
    startDate: "01/15/2026",
    expiryDate: "07/15/2026",
    visitsAuthorized: 12,
    visitsUsed: 2,
    status: "active",
  },
];

export const MOCK_MONTHLY_COLLECTIONS: MonthlyCollection[] = [
  { month: "Sep", collected: 38200, billed: 52400, goal: 45000 },
  { month: "Oct", collected: 41800, billed: 55100, goal: 45000 },
  { month: "Nov", collected: 39600, billed: 50800, goal: 45000 },
  { month: "Dec", collected: 44200, billed: 58300, goal: 45000 },
  { month: "Jan", collected: 36100, billed: 48900, goal: 45000 },
  { month: "Feb", collected: 47800, billed: 61200, goal: 45000 },
  { month: "Mar", collected: 2847, billed: 4200, goal: 4500 },
];

export const MOCK_PROVIDER_STATS: ProviderStats[] = [
  {
    name: "Dr. Sarah Mitchell",
    visits: 142,
    billed: 68400,
    collected: 61200,
    collectionRate: 89,
    noShows: 8,
  },
  {
    name: "Dr. James Roberts",
    visits: 128,
    billed: 59100,
    collected: 51800,
    collectionRate: 87,
    noShows: 11,
  },
  {
    name: "Dr. Emily Chen",
    visits: 118,
    billed: 54200,
    collected: 46900,
    collectionRate: 86,
    noShows: 6,
  },
];

export const MOCK_PAYER_PERFORMANCE: PayerPerformance[] = [
  {
    payer: "Blue Cross Blue Shield",
    claims: 198,
    avgDaysToPay: 18,
    denialRate: 4,
    collectionRate: 94,
  },
  {
    payer: "Aetna",
    claims: 112,
    avgDaysToPay: 24,
    denialRate: 9,
    collectionRate: 88,
  },
  {
    payer: "United Health",
    claims: 89,
    avgDaysToPay: 31,
    denialRate: 12,
    collectionRate: 82,
  },
  {
    payer: "Cigna",
    claims: 67,
    avgDaysToPay: 21,
    denialRate: 6,
    collectionRate: 91,
  },
  {
    payer: "Medicare",
    claims: 44,
    avgDaysToPay: 14,
    denialRate: 3,
    collectionRate: 97,
  },
  {
    payer: "Self-Pay",
    claims: 38,
    avgDaysToPay: 0,
    denialRate: 0,
    collectionRate: 62,
  },
];

export const MOCK_AGING_BUCKETS: AgingBucket[] = [
  { label: "0–30 days", amount: 12400, count: 34 },
  { label: "31–60 days", amount: 8900, count: 22 },
  { label: "61–90 days", amount: 5200, count: 14 },
  { label: "90+ days", amount: 3800, count: 11 },
];

export const DASHBOARD_METRICS = {
  collectedToday: 685,
  revenueGoal: 4200,
  patientsCheckedIn: 3,
  patientsTotal: 15,
  outstandingBalance: 989,
  outstandingCount: 6,
  paymentPlansActive: 7,
  paymentPlansMonthly: 891,
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

// ─── Prescriptions ────────────────────────────────────────────────────────────

export type PrescriptionStatus = "active" | "expired" | "discontinued";

export interface Prescription {
  id: string;
  patientId: string;
  patientName: string;
  drug: string;
  genericName: string;
  dosage: string;
  instructions: string;
  quantity: number;
  refills: number;
  refillsRemaining: number;
  prescribedBy: string;
  prescribedDate: string;
  expiryDate: string;
  status: PrescriptionStatus;
  icd10: string;
  diagnosisDescription: string;
  drugClass: string;
  pharmacyName?: string;
  lastDispensed?: string;
  encounterNoteId?: string;
}

export const MOCK_PRESCRIPTIONS: Prescription[] = [
  {
    id: "RX001",
    patientId: "P001",
    patientName: "Michael Thompson",
    drug: "Triamcinolone Acetonide 0.1% Cream",
    genericName: "Triamcinolone",
    dosage: "0.1% — Apply thin layer",
    instructions:
      "Apply thin layer to affected areas twice daily. Avoid face and groin.",
    quantity: 80,
    refills: 2,
    refillsRemaining: 1,
    prescribedBy: "Dr. Sarah Mitchell",
    prescribedDate: "2025-09-12",
    expiryDate: "2026-09-12",
    status: "active",
    icd10: "L40.0",
    diagnosisDescription: "Psoriasis vulgaris",
    drugClass: "Topical Corticosteroid",
    pharmacyName: "CVS Pharmacy — Royal Oak",
    lastDispensed: "2026-01-15",
  },
  {
    id: "RX002",
    patientId: "P001",
    patientName: "Michael Thompson",
    drug: "Methotrexate 15mg Tablet",
    genericName: "Methotrexate",
    dosage: "15mg — Once weekly",
    instructions:
      "Take 15mg orally every Sunday. Take folic acid 1mg daily except on methotrexate day.",
    quantity: 4,
    refills: 5,
    refillsRemaining: 4,
    prescribedBy: "Dr. Sarah Mitchell",
    prescribedDate: "2025-07-20",
    expiryDate: "2026-07-20",
    status: "active",
    icd10: "L40.0",
    diagnosisDescription: "Psoriasis vulgaris — moderate to severe",
    drugClass: "DMARD / Immunomodulator",
    pharmacyName: "CVS Pharmacy — Royal Oak",
    lastDispensed: "2026-02-10",
  },
  {
    id: "RX003",
    patientId: "P007",
    patientName: "Robert Chen",
    drug: "Tretinoin 0.025% Cream",
    genericName: "Tretinoin",
    dosage: "0.025% — Apply pea-sized amount",
    instructions:
      "Apply pea-sized amount to clean, dry face every night. Use sunscreen daily.",
    quantity: 40,
    refills: 3,
    refillsRemaining: 3,
    prescribedBy: "Dr. Emily Chen",
    prescribedDate: "2025-07-30",
    expiryDate: "2026-07-30",
    status: "active",
    icd10: "L70.0",
    diagnosisDescription: "Acne vulgaris",
    drugClass: "Topical Retinoid",
    pharmacyName: "Walgreens — Dearborn",
    lastDispensed: "2025-12-15",
  },
  {
    id: "RX004",
    patientId: "P007",
    patientName: "Robert Chen",
    drug: "Doxycycline Hyclate 100mg Capsule",
    genericName: "Doxycycline",
    dosage: "100mg — Twice daily",
    instructions:
      "Take one capsule twice daily with food and water. Avoid prolonged sun exposure.",
    quantity: 60,
    refills: 1,
    refillsRemaining: 0,
    prescribedBy: "Dr. Emily Chen",
    prescribedDate: "2025-03-10",
    expiryDate: "2025-09-10",
    status: "expired",
    icd10: "L70.0",
    diagnosisDescription: "Acne vulgaris — inflammatory",
    drugClass: "Antibiotic — Tetracycline",
    pharmacyName: "Walgreens — Dearborn",
    lastDispensed: "2025-06-01",
  },
  {
    id: "RX005",
    patientId: "P008",
    patientName: "Maria Gonzalez",
    drug: "Dupilumab 300mg/2mL Injection",
    genericName: "Dupilumab (Dupixent)",
    dosage: "300mg — Every 2 weeks",
    instructions:
      "Inject subcutaneously in abdomen or thigh every 14 days. Rotate injection sites.",
    quantity: 2,
    refills: 5,
    refillsRemaining: 5,
    prescribedBy: "Dr. Sarah Mitchell",
    prescribedDate: "2026-01-15",
    expiryDate: "2026-07-15",
    status: "active",
    icd10: "L20.9",
    diagnosisDescription: "Atopic dermatitis — moderate to severe",
    drugClass: "Biologic — IL-4/IL-13 Inhibitor",
    pharmacyName: "Specialty Pharmacy — Optum Rx",
    lastDispensed: "2026-02-20",
  },
  {
    id: "RX006",
    patientId: "P008",
    patientName: "Maria Gonzalez",
    drug: "Desonide 0.05% Cream",
    genericName: "Desonide",
    dosage: "0.05% — Apply twice daily",
    instructions:
      "Apply to affected areas twice daily as needed for flares. Maximum 4 weeks continuous use.",
    quantity: 60,
    refills: 2,
    refillsRemaining: 2,
    prescribedBy: "Dr. Sarah Mitchell",
    prescribedDate: "2025-12-01",
    expiryDate: "2026-12-01",
    status: "active",
    icd10: "L20.9",
    diagnosisDescription: "Atopic dermatitis",
    drugClass: "Topical Corticosteroid — Low Potency",
    pharmacyName: "CVS Pharmacy — Warren",
    lastDispensed: "2026-01-28",
  },
  {
    id: "RX007",
    patientId: "P003",
    patientName: "Sarah Thompson",
    drug: "Halobetasol Propionate 0.05% Ointment",
    genericName: "Halobetasol",
    dosage: "0.05% — Apply twice daily",
    instructions:
      "Apply sparingly to plaques twice daily for up to 2 consecutive weeks. Do not occlude.",
    quantity: 45,
    refills: 1,
    refillsRemaining: 1,
    prescribedBy: "Dr. Emily Chen",
    prescribedDate: "2025-11-05",
    expiryDate: "2026-11-05",
    status: "active",
    icd10: "L40.0",
    diagnosisDescription: "Psoriasis — plaque type",
    drugClass: "Topical Corticosteroid — Super High Potency",
    pharmacyName: "Rite Aid — Ann Arbor",
    lastDispensed: "2025-12-02",
  },
  {
    id: "RX008",
    patientId: "P005",
    patientName: "Michael Martinez",
    drug: "Ivermectin 1% Cream",
    genericName: "Ivermectin (Soolantra)",
    dosage: "1% — Apply once daily",
    instructions:
      "Apply pea-sized amount to forehead, chin, nose, and each cheek once daily. Avoid eyes.",
    quantity: 30,
    refills: 3,
    refillsRemaining: 3,
    prescribedBy: "Dr. Emily Chen",
    prescribedDate: "2025-10-15",
    expiryDate: "2026-10-15",
    status: "active",
    icd10: "L71.9",
    diagnosisDescription: "Rosacea",
    drugClass: "Topical Antiparasitic",
    pharmacyName: "CVS Pharmacy — Dearborn",
    lastDispensed: "2026-01-10",
  },
];

// ─── Clinical Images ───────────────────────────────────────────────────────────

export type ImageType =
  | "dermoscopy"
  | "clinical-photo"
  | "lesion-close-up"
  | "before-after";

export interface ClinicalImage {
  id: string;
  patientId: string;
  patientName: string;
  date: string;
  type: ImageType;
  site: string;
  gradient: string; // CSS gradient for placeholder thumbnail
  notes?: string;
  encounterNoteId?: string;
  lesionId?: string;
  tags?: string[];
}

export const MOCK_CLINICAL_IMAGES: ClinicalImage[] = [
  {
    id: "IMG001",
    patientId: "P001",
    patientName: "Michael Thompson",
    date: "2026-03-06",
    type: "dermoscopy",
    site: "Left forearm — L1",
    gradient: "linear-gradient(135deg, #1a1032 0%, #2d1b69 50%, #4a2fa3 100%)",
    notes: "Irregular network pattern, regression areas visible",
    encounterNoteId: "ENC001",
    lesionId: "L1",
    tags: ["atypical", "dermoscopy"],
  },
  {
    id: "IMG002",
    patientId: "P001",
    patientName: "Michael Thompson",
    date: "2026-03-06",
    type: "lesion-close-up",
    site: "Left forearm — L1",
    gradient: "linear-gradient(135deg, #3b1a08 0%, #7c3522 50%, #b85a38 100%)",
    notes:
      "6mm pigmented papule with irregular border. Pre-biopsy documentation.",
    encounterNoteId: "ENC001",
    lesionId: "L1",
    tags: ["pre-biopsy"],
  },
  {
    id: "IMG003",
    patientId: "P001",
    patientName: "Michael Thompson",
    date: "2025-09-12",
    type: "clinical-photo",
    site: "Bilateral forearms — psoriatic plaques",
    gradient: "linear-gradient(135deg, #1c3a5e 0%, #2660a4 50%, #4888d4 100%)",
    notes:
      "Erythematous plaques with silvery scale. Pre-treatment documentation.",
    tags: ["psoriasis", "baseline"],
  },
  {
    id: "IMG004",
    patientId: "P007",
    patientName: "Robert Chen",
    date: "2025-07-30",
    type: "clinical-photo",
    site: "Face — acne",
    gradient: "linear-gradient(135deg, #2d0a0a 0%, #7a1f1f 50%, #c44646 100%)",
    notes:
      "Moderate inflammatory acne — papules and pustules on forehead and chin",
    tags: ["acne", "baseline"],
  },
  {
    id: "IMG005",
    patientId: "P007",
    patientName: "Robert Chen",
    date: "2025-12-15",
    type: "before-after",
    site: "Face — acne — 4 month follow-up",
    gradient: "linear-gradient(135deg, #0f2e1a 0%, #1e5c33 50%, #2e8a4d 100%)",
    notes:
      "Significant improvement after tretinoin + doxycycline. 60% reduction in lesion count.",
    tags: ["acne", "improvement"],
  },
  {
    id: "IMG006",
    patientId: "P008",
    patientName: "Maria Gonzalez",
    date: "2025-06-14",
    type: "clinical-photo",
    site: "Bilateral antecubital fossa",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    notes:
      "Classic eczema distribution — flexural involvement, lichenification present",
    tags: ["eczema", "baseline"],
  },
  {
    id: "IMG007",
    patientId: "P008",
    patientName: "Maria Gonzalez",
    date: "2026-02-20",
    type: "before-after",
    site: "Bilateral antecubital — 2 months post Dupixent",
    gradient: "linear-gradient(135deg, #0d2b1d 0%, #1a5c3e 50%, #28935f 100%)",
    notes:
      "Dramatic improvement post dupilumab initiation. Near-complete clearance.",
    tags: ["eczema", "dupilumab", "improvement"],
  },
  {
    id: "IMG008",
    patientId: "P003",
    patientName: "Sarah Thompson",
    date: "2025-11-05",
    type: "dermoscopy",
    site: "Upper back — psoriatic plaque",
    gradient: "linear-gradient(135deg, #1e1433 0%, #3d2268 50%, #6b3fa8 100%)",
    notes:
      "Dermoscopy shows dotted vessels, white scale — consistent with psoriasis",
    tags: ["psoriasis", "dermoscopy"],
  },
  {
    id: "IMG009",
    patientId: "P001",
    patientName: "Michael Thompson",
    date: "2025-07-20",
    type: "dermoscopy",
    site: "Right deltoid — monitoring",
    gradient: "linear-gradient(135deg, #1a2e1a 0%, #2d5a2d 50%, #408a40 100%)",
    notes:
      "Regular symmetric network — benign melanocytic nevus. Continue monitoring.",
    tags: ["nevus", "monitoring"],
  },
];

// ─── Clinical / Dermatology Models ────────────────────────────────────────────

export interface LesionABCDE {
  asymmetry: boolean;
  border: boolean;
  color: boolean;
  diameter: boolean;
  evolving: boolean;
}

export interface EncounterLesion {
  id: string;
  x: number; // 0–1 normalized canvas position
  y: number;
  view: "front" | "back";
  label: string; // e.g. "L1", "L2"
  site: string; // e.g. "Left forearm"
  description: string; // free text
  size: string; // e.g. "6mm"
  morphology: string; // e.g. "Papule", "Macule", "Plaque"
  abcde: LesionABCDE;
  action: "monitor" | "biopsy" | "treat" | "none";
  cryotherapy?: boolean;
}

export type BiopsyStatus =
  | "in-transit"
  | "at-lab"
  | "resulted"
  | "communicated";
export type ResultSeverity = "benign" | "dysplastic" | "malignant";

export interface BiopsySpecimen {
  id: string;
  patientId: string;
  patientName: string;
  patientInitials: string;
  siteDescription: string;
  procedureType: "Shave Bx" | "Punch Bx" | "Excision" | "Cryotherapy";
  dateSent: string;
  daysOut: number;
  status: BiopsyStatus;
  result?: string;
  resultSeverity?: ResultSeverity;
  cptCode: string;
  pathologist?: string;
}

export type EncounterStatus =
  | "not-started"
  | "in-progress"
  | "ready-to-sign"
  | "signed";

export interface EncounterNote {
  id: string;
  patientId: string;
  patientName: string;
  patientInitials: string;
  appointmentId: string;
  provider: string;
  chiefComplaint: string;
  hpi: string;
  lesions: EncounterLesion[];
  procedures: string[];
  assessment: string;
  plan: string;
  icd10Codes: { code: string; description: string }[];
  cptCodes: { code: string; description: string; units: number }[];
  prescriptions: Prescription[];
  status: EncounterStatus;
  signedAt?: string;
}

// ─── Clinical Mock Data ────────────────────────────────────────────────────────

export const MOCK_BIOPSY_SPECIMENS: BiopsySpecimen[] = [
  {
    id: "BX001",
    patientId: "P001",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    siteDescription: "Left forearm — 6mm pigmented papule",
    procedureType: "Shave Bx",
    dateSent: "2026-02-28",
    daysOut: 6,
    status: "at-lab",
    cptCode: "11102",
    pathologist: "Dermpath Associates",
  },
  {
    id: "BX002",
    patientId: "P002",
    patientName: "Sarah Johnson",
    patientInitials: "SJ",
    siteDescription: "Right shoulder — 4mm erythematous papule",
    procedureType: "Punch Bx",
    dateSent: "2026-03-01",
    daysOut: 5,
    status: "in-transit",
    cptCode: "11104",
    pathologist: "Dermpath Associates",
  },
  {
    id: "BX003",
    patientId: "P003",
    patientName: "David Kim",
    patientInitials: "DK",
    siteDescription: "Posterior scalp — 8mm plaque with ulceration",
    procedureType: "Excision",
    dateSent: "2026-02-22",
    daysOut: 12,
    status: "resulted",
    result: "Squamous cell carcinoma, well-differentiated. Margins clear.",
    resultSeverity: "malignant",
    cptCode: "11305",
    pathologist: "Dermpath Associates",
  },
  {
    id: "BX004",
    patientId: "P004",
    patientName: "Emily Rodriguez",
    patientInitials: "ER",
    siteDescription: "Upper back — 5mm atypical nevus",
    procedureType: "Punch Bx",
    dateSent: "2026-02-25",
    daysOut: 9,
    status: "communicated",
    result:
      "Dysplastic nevus with moderate atypia. Complete removal recommended.",
    resultSeverity: "dysplastic",
    cptCode: "11104",
    pathologist: "Dermpath Associates",
  },
];

export const MOCK_ENCOUNTER_NOTES: EncounterNote[] = [
  {
    id: "ENC001",
    patientId: "P001",
    patientName: "Michael Thompson",
    patientInitials: "MT",
    appointmentId: "A001",
    provider: "Dr. Sarah Mitchell",
    chiefComplaint:
      "Annual skin check + follow-up for pigmented lesion on left forearm",
    hpi: "42-year-old male presents for annual total body skin exam. Patient reports a growing dark spot on left forearm noticed ~3 months ago. No bleeding or itching. Fitzpatrick type II. No personal or family history of melanoma.",
    lesions: [
      {
        id: "L1",
        x: 0.38,
        y: 0.52,
        view: "front",
        label: "L1",
        site: "Left forearm",
        description: "6mm pigmented papule, irregular border",
        size: "6mm",
        morphology: "Papule",
        abcde: {
          asymmetry: true,
          border: true,
          color: false,
          diameter: true,
          evolving: true,
        },
        action: "biopsy",
      },
      {
        id: "L2",
        x: 0.55,
        y: 0.3,
        view: "front",
        label: "L2",
        site: "Right deltoid",
        description: "3mm flesh-colored papule, smooth, symmetric",
        size: "3mm",
        morphology: "Papule",
        abcde: {
          asymmetry: false,
          border: false,
          color: false,
          diameter: false,
          evolving: false,
        },
        action: "monitor",
      },
    ],
    procedures: [
      "Shave Biopsy — Left Forearm (CPT 11102)",
      "Dermoscopy (CPT 96904)",
    ],
    assessment:
      "1. Atypical pigmented lesion, left forearm — shave biopsy performed\n2. Benign-appearing papule, right deltoid — monitor\n3. Actinic damage, moderate",
    plan: "Await pathology results (5–7 business days). Patient notified of call-back protocol. Sunscreen counseling provided. RTC 3 months for follow-up skin check.",
    icd10Codes: [
      { code: "D48.5", description: "Neoplasm of uncertain behavior of skin" },
      { code: "L57.0", description: "Actinic keratosis" },
    ],
    cptCodes: [
      { code: "11102", description: "Shave Biopsy, first lesion", units: 1 },
      {
        code: "96904",
        description: "Whole body photography / dermoscopy",
        units: 1,
      },
      {
        code: "99213",
        description: "Office visit, moderate complexity",
        units: 1,
      },
    ],
    prescriptions: [],
    status: "ready-to-sign",
  },
];
